import { useEffect, useMemo, useState } from 'react';
import { Button, Card, CardSubtitle, CardTitle } from '@keobook/ui';
import { api } from '../lib/api';
import { useMoneyFormatter } from '../lib/useCurrency';
import { PeriodPicker } from './PeriodPicker';
import { diffRows, diffValue, formatDeltaPct, type DiffRow } from '../lib/reportDiff';
import {
  lastMonthRange,
  monthRange,
  type DateRange,
  type PeriodBounds,
} from '../lib/reportPeriods';

/** A normalized report: labelled sections of account rows + a set of subtotals. */
type CmpRow = { accountId: string | null; code: string; name: string; amountCents: number };
type CmpSection = { title: string; rows: CmpRow[] };
type Normalized = { sections: CmpSection[]; totals: Array<{ label: string; cents: number }> };

type ReportType = 'pl' | 'bs' | 'cf' | 'tb' | 'gl' | 'ar';

type ReportConfig = {
  id: ReportType;
  label: string;
  /** True for point-in-time reports (Balance Sheet, Trial Balance, A/R) whose
   *  "period" is really its end date — we compare source-end vs target-end. */
  pointInTime: boolean;
  fetch: (range: DateRange) => Promise<Normalized>;
};

const AR_BUCKETS: Array<{ key: string; label: string }> = [
  { key: 'current', label: 'Current' },
  { key: '1-30', label: '1–30 days' },
  { key: '31-60', label: '31–60 days' },
  { key: '61-90', label: '61–90 days' },
  { key: '90+', label: '90+ days' },
];

const REPORTS: ReportConfig[] = [
  {
    id: 'pl',
    label: 'Profit & Loss',
    pointInTime: false,
    fetch: async ({ from, to }) => {
      const r = await api.getIncomeStatement({ from, to });
      return {
        sections: [
          { title: 'Revenue', rows: r.revenue },
          { title: 'Cost of Revenue', rows: r.costOfRevenue },
          { title: 'Operating Expenses', rows: r.expenses },
        ],
        totals: [
          { label: 'Total revenue', cents: r.totalRevenueCents },
          { label: 'Gross profit', cents: r.grossProfitCents },
          { label: 'Total expenses', cents: r.totalExpensesCents },
          { label: 'Net income', cents: r.netIncomeCents },
        ],
      };
    },
  },
  {
    id: 'bs',
    label: 'Balance Sheet',
    pointInTime: true,
    fetch: async ({ from, to }) => {
      const r = await api.getBalanceSheet({ asOf: to, from });
      return {
        sections: [
          { title: 'Assets', rows: r.assets },
          { title: 'Liabilities', rows: r.liabilities },
          { title: 'Equity', rows: r.equity },
        ],
        totals: [
          { label: 'Total assets', cents: r.totalAssetsCents },
          { label: 'Total liabilities', cents: r.totalLiabilitiesCents },
          { label: 'Total equity', cents: r.totalEquityCents },
        ],
      };
    },
  },
  {
    id: 'cf',
    label: 'Cash Flow',
    pointInTime: false,
    fetch: async ({ from, to }) => {
      const r = await api.getCashFlow({ from, to });
      return {
        sections: [
          { title: 'Operating', rows: r.operating.items },
          { title: 'Investing', rows: r.investing.items },
          { title: 'Financing', rows: r.financing.items },
        ],
        totals: [
          { label: 'Net income', cents: r.netIncomeCents },
          { label: 'Net cash from operating', cents: r.operating.totalCents },
          { label: 'Net cash from investing', cents: r.investing.totalCents },
          { label: 'Net cash from financing', cents: r.financing.totalCents },
          { label: 'Net change in cash', cents: r.netChangeInCashCents },
          { label: 'Beginning cash', cents: r.beginningCashCents },
          { label: 'Ending cash', cents: r.endingCashCents },
        ],
      };
    },
  },
  {
    id: 'tb',
    label: 'Trial Balance',
    pointInTime: true,
    fetch: async ({ from, to }) => {
      const r = await api.getTrialBalance({ asOf: to, from });
      return {
        // Net each account to a single signed balance (debit positive) so it
        // diffs as one column instead of two.
        sections: [
          {
            title: 'Accounts (net balance, debit +)',
            rows: r.rows.map((row) => ({
              accountId: row.accountId,
              code: row.code,
              name: row.name,
              amountCents: row.debitCents - row.creditCents,
            })),
          },
        ],
        totals: [
          { label: 'Total debits', cents: r.totalDebitCents },
          { label: 'Total credits', cents: r.totalCreditCents },
        ],
      };
    },
  },
  {
    id: 'gl',
    label: 'General Ledger',
    pointInTime: true,
    fetch: async ({ from, to }) => {
      const r = await api.getGlSummary({ from, to });
      return {
        // Account-summary comparison (closing balance per account) — a
        // transaction-level ledger diff isn't meaningful across periods.
        sections: [
          {
            title: 'Accounts (closing balance)',
            rows: r.accounts.map((a) => ({
              accountId: a.accountId,
              code: a.code,
              name: a.name,
              amountCents: a.closingCents,
            })),
          },
        ],
        totals: [],
      };
    },
  },
  {
    id: 'ar',
    label: 'A/R Aging',
    pointInTime: true,
    fetch: async ({ to }) => {
      const r = await api.getArAging({ asOf: to });
      return {
        sections: [
          {
            title: 'Aging buckets',
            rows: AR_BUCKETS.map((b) => ({
              accountId: b.key,
              code: '',
              name: b.label,
              amountCents: r.grandTotals[b.key] ?? 0,
            })),
          },
        ],
        totals: [{ label: 'Total A/R', cents: r.grandTotalCents }],
      };
    },
  },
];

/** Default source = same month one year before the (last-month) target. */
function defaultPeriods(): { source: DateRange; target: DateRange } {
  const target = lastMonthRange();
  const y = Number(target.from.slice(0, 4)) - 1;
  const source = monthRange(`${y}-${target.from.slice(5, 7)}`);
  return { source, target };
}

function Amount({ cents, muted }: { cents: number; muted?: boolean }) {
  const fmt = useMoneyFormatter();
  return (
    <span style={{ color: muted ? 'var(--kb-gray-400)' : undefined }}>{fmt(cents)}</span>
  );
}

function Delta({ cents, pct }: { cents: number; pct: number | null }) {
  const fmt = useMoneyFormatter();
  const color =
    cents > 0
      ? 'var(--kb-green-600, #16a34a)'
      : cents < 0
        ? 'var(--kb-red-600, #dc2626)'
        : 'var(--kb-gray-400)';
  const sign = cents > 0 ? '+' : cents < 0 ? '−' : '';
  return (
    <span style={{ color }}>
      {sign}
      {fmt(Math.abs(cents))} <span style={{ fontSize: 12 }}>({formatDeltaPct(pct)})</span>
    </span>
  );
}

export function ReportComparison({ bounds }: { bounds: PeriodBounds | null }) {
  const [reportType, setReportType] = useState<ReportType>('pl');
  const init = useMemo(() => defaultPeriods(), []);
  const [source, setSource] = useState<DateRange>(init.source);
  const [target, setTarget] = useState<DateRange>(init.target);
  const [srcData, setSrcData] = useState<Normalized | null>(null);
  const [tgtData, setTgtData] = useState<Normalized | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const config = REPORTS.find((r) => r.id === reportType)!;
  const rangesValid =
    source.from &&
    source.to &&
    target.from &&
    target.to &&
    source.from <= source.to &&
    target.from <= target.to;

  useEffect(() => {
    if (!rangesValid) return;
    let alive = true;
    setLoading(true);
    setError(null);
    Promise.all([config.fetch(source), config.fetch(target)])
      .then(([s, t]) => {
        if (!alive) return;
        setSrcData(s);
        setTgtData(t);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        setError(err instanceof Error ? err.message : 'Could not load the comparison.');
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [reportType, source, target, rangesValid, config]);

  const periodLabel = (r: DateRange) =>
    config.pointInTime ? `as of ${r.to}` : `${r.from} → ${r.to}`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Report-type selector */}
      <div
        role="tablist"
        aria-label="Comparison report"
        style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
      >
        {REPORTS.map((r) => (
          <Button
            key={r.id}
            size="sm"
            role="tab"
            aria-selected={reportType === r.id}
            variant={reportType === r.id ? 'primary' : 'ghost'}
            onClick={() => setReportType(r.id)}
          >
            {r.label}
          </Button>
        ))}
      </div>

      {/* Two independent, free-form pickers */}
      <div
        style={{
          display: 'grid',
          gap: '1rem',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        }}
      >
        <PeriodPicker
          value={source}
          onChange={setSource}
          bounds={bounds}
          title="Source period"
          subtitle={
            config.pointInTime
              ? 'Point-in-time report — compared as of this period’s “to” date.'
              : 'The baseline period to compare against.'
          }
        />
        <PeriodPicker
          value={target}
          onChange={setTarget}
          bounds={bounds}
          title="Target period"
          subtitle={
            config.pointInTime
              ? 'Point-in-time report — compared as of this period’s “to” date.'
              : 'The period to compare to the source.'
          }
        />
      </div>

      {error && (
        <div role="alert" className="kb-form-error">
          {error}
        </div>
      )}

      <Card>
        <CardTitle>
          {config.label}: {periodLabel(source)} vs {periodLabel(target)}
        </CardTitle>
        <CardSubtitle>
          Source and target are fully independent — compare any period to any other. Δ is target
          minus source; the percent is of the source amount.
        </CardSubtitle>

        {loading && (
          <p className="kb-muted" style={{ marginTop: 10 }}>
            Loading comparison…
          </p>
        )}

        {!loading && srcData && tgtData && (
          <table className="kb-report" style={{ marginTop: 12, width: '100%' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Account</th>
                <th className="kb-report__amt">Source</th>
                <th className="kb-report__amt">Target</th>
                <th className="kb-report__amt">Change</th>
              </tr>
            </thead>
            <tbody>
              {srcData.sections.map((srcSection, i) => {
                const tgtSection = tgtData.sections[i] ?? { title: srcSection.title, rows: [] };
                const rows: DiffRow[] = diffRows(
                  srcSection.rows,
                  tgtSection.rows,
                  (r) => r.accountId ?? r.code,
                  (r) => r.amountCents,
                  (r) => ({ code: r.code, name: r.name }),
                );
                return (
                  <FragmentSection key={srcSection.title} title={srcSection.title} rows={rows} />
                );
              })}
              {srcData.totals.length > 0 && (
                <>
                  <tr>
                    <td colSpan={4} style={{ paddingTop: 12 }} />
                  </tr>
                  {srcData.totals.map((t, i) => {
                    const tgt = tgtData.totals[i]?.cents ?? 0;
                    const d = diffValue(t.cents, tgt);
                    return (
                      <tr key={t.label} style={{ fontWeight: 600 }}>
                        <td>{t.label}</td>
                        <td className="kb-report__amt">
                          <Amount cents={d.sourceCents} />
                        </td>
                        <td className="kb-report__amt">
                          <Amount cents={d.targetCents} />
                        </td>
                        <td className="kb-report__amt">
                          <Delta cents={d.deltaCents} pct={d.deltaPct} />
                        </td>
                      </tr>
                    );
                  })}
                </>
              )}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function FragmentSection({ title, rows }: { title: string; rows: DiffRow[] }) {
  return (
    <>
      <tr>
        <td colSpan={4} style={{ fontWeight: 600, paddingTop: 14, color: 'var(--kb-gray-900)' }}>
          {title}
        </td>
      </tr>
      {rows.length === 0 ? (
        <tr>
          <td colSpan={4} className="kb-muted" style={{ fontSize: 13 }}>
            No activity in either period.
          </td>
        </tr>
      ) : (
        rows.map((r) => (
          <tr key={r.key}>
            <td>
              {r.code ? <span className="kb-muted">{r.code}</span> : null} {r.name}
            </td>
            <td className="kb-report__amt">
              <Amount cents={r.sourceCents} muted={r.sourceCents === 0} />
            </td>
            <td className="kb-report__amt">
              <Amount cents={r.targetCents} muted={r.targetCents === 0} />
            </td>
            <td className="kb-report__amt">
              <Delta cents={r.deltaCents} pct={r.deltaPct} />
            </td>
          </tr>
        ))
      )}
    </>
  );
}
